// Import flag-icons CSS (add this at the end of imports)
import "flag-icons/css/flag-icons.min.css";

import { t } from "@lingui/macro";
import { Plus } from "@phosphor-icons/react";
import { useBreakpoint, useTouchOptimization } from "@stylingcv/hooks";
import type {
  Award,
  Certification,
  CustomSection,
  Education,
  Experience,
  Hobby,
  Interest,
  Language,
  MostProudOf,
  Profile,
  Project,
  Publication,
  Reference,
  Skill,
  Volunteer,
} from "@stylingcv/schema";
import { Button, ScrollArea } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { SignupModal } from "@/client/components/auth/signup-modal";
import { useToast } from "@/client/hooks/use-toast";
import { usePrintResume } from "@/client/services/resume/print";
import { useAuthStore } from "@/client/stores/auth";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

import type { BuilderTab, DocumentType } from "../../_components/builder-tabs";
import { DeleteSectionDialog } from "./dialogs/delete-section";
import { AnalysisSection } from "./sections/analysis";
import { BasicsSection } from "./sections/basics";
import { RoastSection } from "./sections/roast";
import { CoverLetterContentSection } from "./sections/cover-letter-content";
import { MyMindSection } from "./sections/my-mind";
import { ResumeInfoSection } from "./sections/resume-info";
import { SectionBase } from "./sections/shared/section-base";
import { SignatureSection } from "./sections/signature";
import { SummarySection } from "./sections/summary";

type LeftSidebarProps = {
  activeTab?: BuilderTab;
  activeDocument?: DocumentType;
  singleSectionMode?: boolean;
};

export const LeftSidebar = ({ activeTab, activeDocument, singleSectionMode }: LeftSidebarProps = {}) => {
  const { isMobile } = useBreakpoint();
  const { buttonProps, getTouchClasses } = useTouchOptimization();

  const activeSectionId = useBuilderStore((state) => state.activeSectionId);
  const { toast } = useToast();
  const navigate = useNavigate();

  // State variables to control the auth modal
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authModalType, setAuthModalType] = useState<"save" | "download">("save");

  // Check if user is logged in
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { subscriptionTier } = useAuthStore();

  // Get PDF print function
  const { printResume } = usePrintResume();

  // Get resume ID and user data for the modal
  const id = useResumeStore((state) => state.resume.id);
  const userData = useResumeStore((state) => {
    try {
      return {
        name: state.resume.data.basics.name || "",
        email: state.resume.data.basics.email || "",
      };
    } catch {
      return {
        name: "",
        email: "",
      };
    }
  });

  const addSection = useResumeStore((state) => state.addSection);
  const customSections = useResumeStore((state) => {
    try {
      return state.resume.data.sections.custom || {};
    } catch {
      return {};
    }
  });

  // Check if this is a cover letter document
  const isCoverLetter = useResumeStore((state) => {
    try {
      return (state.resume as any).type === "coverLetter";
    } catch {
      return false;
    }
  });

  const safeAddSection = () => {
    try {
      addSection();
    } catch {
      // Silent fail
    }
  };

  // Handle PDF export action
  const handleExportPdf = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!isLoggedIn) {
      setAuthModalType("download");
      setShowAuthModal(true);
      return;
    }

    // Redirect FREE tier users to the pricing page
    if (subscriptionTier === "FREE") {
      navigate("/pricing");
      return;
    }

    // Generate PDF for paid users
    printResume({ id })
      .then(({ url }) => {
        window.open(url, "_blank")?.focus();
      })
      .catch(() => {
        toast({
          variant: "error",
          title: t`Error`,
          description: t`Failed to generate PDF. Please try again.`,
        });
      });
  };

  // Handles modal closing after successful registration
  const onCloseModal = () => {
    setShowAuthModal(false);

    // If user just registered and is now logged in, handle the original action
    if (isLoggedIn) {
      if (authModalType === "save") {
        toast({
          variant: "default",
          title: t`Resume Saved`,
          description: t`Your resume has been saved to your account.`,
        });
      } else if (authModalType === "download") {
        // Handle PDF download after signup based on template access
        if (subscriptionTier === "FREE") {
          toast({
            variant: "info",
            title: t`PDF Export Requires Upgrade`,
            description: t`PDF export is available in paid plans. You can upgrade your account to access this feature.`,
          });

          const goToPricing = window.confirm(
            t`Would you like to visit the pricing page to upgrade your account?`,
          );
          if (goToPricing) {
            navigate("/pricing");
          }
        } else {
          // For paid tiers, proceed with the PDF download
          handleExportPdf({ preventDefault: () => {} } as React.MouseEvent);
        }
      }
    }
  };

  // Render individual section based on ID
  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "basics": {
        return <BasicsSection />;
      }
      case "summary": {
        return <SummarySection />;
      }
      case "profiles": {
        return (
          <SectionBase<Profile>
            id="profiles"
            title={(item) => item.network}
            description={(item) => item.username}
          />
        );
      }
      case "experience": {
        return (
          <SectionBase<Experience>
            id="experience"
            title={(item) => item.position || item.company}
            description={(item) => item.company}
            date={(item) => item.date || undefined}
          />
        );
      }
      case "education": {
        return (
          <SectionBase<Education>
            id="education"
            title={(item) => item.studyType || item.area || item.institution}
            description={(item) => item.institution}
            date={(item) => item.date || undefined}
          />
        );
      }
      case "skills": {
        return (
          <SectionBase<Skill>
            id="skills"
            title={(item) => item.name}
            description={(item) => item.description}
            level={(item) => item.level}
            keywords={(item) => item.keywords}
          />
        );
      }
      case "languages": {
        return (
          <SectionBase<Language>
            id="languages"
            title={(item) => item.name}
            description={(item) => item.description}
            level={(item) => item.level}
          />
        );
      }
      case "awards": {
        return (
          <SectionBase<Award>
            id="awards"
            title={(item) => item.title}
            description={(item) => item.awarder}
          />
        );
      }
      case "certifications": {
        return (
          <SectionBase<Certification>
            id="certifications"
            title={(item) => item.name}
            description={(item) => item.issuer}
          />
        );
      }
      case "interests": {
        return (
          <SectionBase<Interest>
            id="interests"
            title={(item) => item.name}
            description={(item) => {
              if (item.keywords && item.keywords.length > 0)
                return `${item.keywords.length} keywords`;
              return;
            }}
          />
        );
      }
      case "hobbies": {
        return (
          <SectionBase<Hobby>
            id="hobbies"
            title={(item) => item.name}
            description={(item) => item.description}
            icon={(item) => item.icon || undefined}
            iconFamily="hobby"
          />
        );
      }
      case "projects": {
        return (
          <SectionBase<Project>
            id="projects"
            title={(item) => item.name}
            description={(item) => item.description}
          />
        );
      }
      case "publications": {
        return (
          <SectionBase<Publication>
            id="publications"
            title={(item) => item.name}
            description={(item) => item.publisher}
          />
        );
      }
      case "volunteer": {
        return (
          <SectionBase<Volunteer>
            id="volunteer"
            title={(item) => item.organization}
            description={(item) => item.position}
          />
        );
      }
      case "mostProudOf": {
        return (
          <SectionBase<MostProudOf>
            id="mostProudOf"
            title={(item) => item.name}
            description={(item) => item.description}
            icon={(item) => item.icon || undefined}
            iconFamily="icomoon"
          />
        );
      }
      case "myMind": {
        return <MyMindSection />;
      }
      case "references": {
        return (
          <SectionBase<Reference>
            id="references"
            title={(item) => item.name}
            description={(item) => item.summary}
          />
        );
      }
      case "signature": {
        return <SignatureSection />;
      }
      case "coverLetterContent": {
        // Import and render cover letter content section
        return <CoverLetterContentSection />;
      }
      default: {
        return null;
      }
    }
  };

  // Mobile Layout - Single Page View with All Sections
  if (isMobile && !singleSectionMode) {
    // Define all available sections in order - filter for cover letters
    const resumeSections = [
      "basics",
      "summary",
      "profiles",
      "experience",
      "education",
      "skills",
      "languages",
      "projects",
      "certifications",
      "awards",
      "publications",
      "volunteer",
      "interests",
      "hobbies",
      "mostProudOf",
      "myMind",
      "references",
      "signature",
    ];

    // Cover letters only show basics, cover letter content, and signature
    const coverLetterSections = ["basics", "coverLetterContent", "signature"];

    const allSections = isCoverLetter ? coverLetterSections : resumeSections;

    return (
      <div className="relative z-20 flex h-full flex-col bg-[#faf9f5] text-[#0A1628]">
        {/* All Sections in Single Scrollable View */}
        <ScrollArea className="relative z-10 flex-1">
          <div className="space-y-4 p-4 pb-20">
            {/* Resume Info Section (Always first) — kept on dark bg, component uses text-white */}
            <div className="rounded-xl bg-[#0A1628] p-4 shadow-md">
              <ResumeInfoSection />
            </div>

            {/* Render all sections */}
            {allSections.map((sectionId) => {
              const sectionContent = renderSection(sectionId);
              if (!sectionContent) return null;

              return (
                <div key={sectionId} id={`mobile-section-${sectionId}`} className="overflow-hidden rounded-xl border border-black/[0.07] bg-white shadow-sm [&>div]:h-auto [&>div]:overflow-visible">
                  {sectionContent}
                </div>
              );
            })}

            {/* Custom Sections */}
            {Object.entries(customSections).map(([id, { name }]) => (
              <div key={id} id={`mobile-section-custom-${id}`} className="overflow-hidden rounded-xl border border-black/[0.07] bg-white shadow-sm [&>div]:h-auto [&>div]:overflow-visible">
                <SectionBase<CustomSection>
                  id={`custom.${id}`}
                  title={(item) => item.name}
                  description={(item) => item.description}
                />
              </div>
            ))}

            {/* Add Section Button */}
            <div className="mb-8 flex justify-center pt-4">
              <Button
                {...buttonProps}
                variant="default"
                className={getTouchClasses(
                  "flex w-full items-center justify-center gap-3 rounded-xl bg-[#0A1628] px-6 py-4 font-bold text-white transition-all",
                )}
                onClick={safeAddSection}
              >
                <Plus size={20} weight="bold" />
                <span className="text-xs font-semibold uppercase tracking-wider">{t`Add Custom Section`}</span>
              </Button>
            </div>

            {/* Bottom spacing for mobile safe area */}
            <div className="h-20" />
          </div>
        </ScrollArea>

        {/* Signup Prompt Modal */}
        <SignupModal
          transferGuestResume
          isOpen={showAuthModal}
          prefilledEmail={userData.email}
          onClose={onCloseModal}
        />

        {/* Delete Section Dialog - CRITICAL FOR MOBILE */}
        <DeleteSectionDialog />
      </div>
    );
  }

  // Desktop Layout — single-section focused view
  const effectiveSectionId = activeTab === "analysis" ? "analysis" : activeTab === "roast" ? "roast" : activeSectionId;

  return (
    <div className={cn("flex h-full flex-col", singleSectionMode ? "bg-[#faf9f5]" : "bg-[#F5F5F5]")}>
      {/* Section Content — SectionBase components own their own header */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {effectiveSectionId === "analysis" ? (
          <AnalysisSection />
        ) : effectiveSectionId === "roast" ? (
          <RoastSection />
        ) : activeDocument === "cover-letter" || isCoverLetter ? (
          <div className="flex-1 overflow-y-auto">
            <CoverLetterContentSection autoOpenModal={activeDocument === "cover-letter"} />
            {/* Signature — strip its own h-full/overflow so it flows naturally */}
            <div className="[&>section]:h-auto [&>section]:overflow-visible border-t border-[var(--border,#e5e5e5)]">
              <SignatureSection />
            </div>
          </div>
        ) : (
          renderSection(effectiveSectionId)
        )}
      </div>

      <DeleteSectionDialog />
    </div>
  );
};
